import React, { useState, useEffect } from 'react';
import { Mail, Settings as GraphIcon, Save, Loader, AlertTriangle, CheckCircle } from 'lucide-react';
import { getEmailSettings, updateEmailSettings } from '../../services/settingsapi';
import ConfirmationDialog from '../ConfirmationDialog';
import { SettingsSection, InputField } from '../SettingsUIComponents';

const EmailSettings = ({ canEditSettings, canViewSettings }) => {
  const [emailSettings, setEmailSettings] = useState({
    GRAPH_TENANT_ID: '',
    GRAPH_CLIENT_ID: '',
    GRAPH_CLIENT_SECRET: '',
    GRAPH_MAILBOX: '',
    REPORT_RECIPIENT_EMAIL: '',
    ADMIN_EMAIL: '',
  });
  const [emailErrors, setEmailErrors] = useState({});
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailMessage, setEmailMessage] = useState({ type: '', text: '' });
  const [confirmationDialog, setConfirmationDialog] = useState({
    open: false,
    title: '',
    message: '',
    onConfirm: null
  });

  // Fetch email settings on component mount
  useEffect(() => {
    const fetchEmailSettings = async () => {
      try {
        setEmailLoading(true);
        const data = await getEmailSettings();
        setEmailSettings(data);
        setEmailMessage({ type: '', text: '' });
      } catch (error) {
        console.error('Error fetching email settings:', error);
        setEmailMessage({
          type: 'error',
          text: 'Error loading email settings: ' + error.message
        });
      } finally {
        setEmailLoading(false);
      }
    };

    if (canViewSettings) {
      fetchEmailSettings();
    }
  }, [canViewSettings]);

  // Email validation function
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Validate email settings
  const validateEmailSettings = () => {
    const newErrors = {};

    if (!emailSettings.GRAPH_TENANT_ID.trim()) {
      newErrors.GRAPH_TENANT_ID = "Tenant ID is required";
  }
  
  if (!emailSettings.GRAPH_CLIENT_ID.trim()) {
      newErrors.GRAPH_CLIENT_ID = "Client ID is required";
  }
  
  if (!emailSettings.GRAPH_CLIENT_SECRET.trim()) {
      newErrors.GRAPH_CLIENT_SECRET = "Client Secret is required";
  }
  
  if (!emailSettings.GRAPH_MAILBOX.trim()) {
      newErrors.GRAPH_MAILBOX = "Mailbox is required";
  } else if (!validateEmail(emailSettings.GRAPH_MAILBOX)) {
      newErrors.GRAPH_MAILBOX = "Please enter a valid mailbox";
  }
  
  if (!emailSettings.ADMIN_EMAIL.trim()) {
      newErrors.ADMIN_EMAIL = "Admin email is required";
  } else if (!validateEmail(emailSettings.ADMIN_EMAIL)) {
      newErrors.ADMIN_EMAIL = "Please enter a valid email";
  }
  
  if (!emailSettings.REPORT_RECIPIENT_EMAIL.trim()) {
      newErrors.REPORT_RECIPIENT_EMAIL = "Report recipient email is required";
  } else if (!validateEmail(emailSettings.REPORT_RECIPIENT_EMAIL)) {
      newErrors.REPORT_RECIPIENT_EMAIL = "Please enter a valid email";
  }

    setEmailErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Save email settings
  const saveEmailSettings = async () => {
    if (!validateEmailSettings()) {
      return;
    }

    if (!canEditSettings) {
      setEmailMessage({
        type: 'error',
        text: 'You do not have permission to edit settings'
      });
      return;
    }

    setConfirmationDialog({
      open: true,
      title: 'Confirm Email Settings',
      message: 'Are you sure you want to save these email configuration changes? This will affect all email notifications and communications.',
      onConfirm: async () => {
        try {
          setEmailLoading(true);
          const data = await updateEmailSettings(emailSettings);
          setEmailSettings(data);
          setEmailMessage({
            type: 'success',
            text: 'Email settings saved successfully!'
          });

          setTimeout(() => setEmailMessage({ type: '', text: '' }), 5000);
        } catch (error) {
          console.error('Error saving email settings:', error);
          setEmailMessage({
            type: 'error',
            text: 'Error saving email settings: ' + error.message
          });
        } finally {
          setEmailLoading(false);
        }
      }
    });
  };

  return (
    <>
      <div className="settings-email">
        <div className="settings-email-grid">
          <SettingsSection
            icon={Mail}
            title="Email Configuration"
            description="Configure Microsoft Graph email settings and notification recipients."
          >
            <div className="settings-email-fields">
              <InputField
                label="Graph Mailbox"
                type="email"
                value={emailSettings.GRAPH_MAILBOX}
                onChange={(e) => {
                  setEmailSettings(prevSettings => ({
                    ...prevSettings,
                    GRAPH_MAILBOX: e.target.value
                  }));

                  if (emailErrors.GRAPH_MAILBOX) {
                    setEmailErrors(prevErrors => ({ ...prevErrors, GRAPH_MAILBOX: '' }));
                  }
                }}
                error={emailErrors.GRAPH_MAILBOX}
                placeholder="talenttalks@vlink-info.com"
                helpText="Microsoft Graph mailbox used to send emails"
                canEdit={canEditSettings}
              />
              <InputField
                label="Admin Email"
                type="email"
                value={emailSettings.ADMIN_EMAIL}
                onChange={(e) => {
                  setEmailSettings(prevSettings => ({
                    ...prevSettings,
                    ADMIN_EMAIL: e.target.value
                  }));

                  if (emailErrors.ADMIN_EMAIL) {
                    setEmailErrors(prevErrors => ({ ...prevErrors, ADMIN_EMAIL: '' }));
                  }
                }}
                error={emailErrors.ADMIN_EMAIL}
                placeholder="admin@talenttalks.com"
                helpText="Receives system notifications and alerts"
                canEdit={canEditSettings}
              />
              <InputField
                label="Report Recipient Email"
                type="email"
                value={emailSettings.REPORT_RECIPIENT_EMAIL}
                onChange={(e) => {
                  setEmailSettings(prevSettings => ({
                    ...prevSettings,
                    REPORT_RECIPIENT_EMAIL: e.target.value
                  }));

                  if (emailErrors.REPORT_RECIPIENT_EMAIL) {
                    setEmailErrors(prevErrors => ({ ...prevErrors, REPORT_RECIPIENT_EMAIL: '' }));
                  }
                }}
                error={emailErrors.REPORT_RECIPIENT_EMAIL}
                placeholder="reports@talenttalks.com"
                helpText="Email for sending interview reports"
                canEdit={canEditSettings}
              />
            </div>
          </SettingsSection>

          <SettingsSection
            icon={GraphIcon}
            title="Microsoft Graph Configuration"
            description="Configure Microsoft Graph email settings and notification recipients."
          >
            <div className="settings-email-fields">
              <InputField
                label="Tenant ID"
                value={emailSettings.GRAPH_TENANT_ID}
                onChange={(e) => {
                  setEmailSettings(prevSettings => ({
                    ...prevSettings,
                    GRAPH_TENANT_ID: e.target.value
                  }));

                  if (emailErrors.GRAPH_TENANT_ID) {
                    setEmailErrors(prevErrors => ({ ...prevErrors, GRAPH_TENANT_ID: '' }));
                  }
                }}
                error={emailErrors.GRAPH_TENANT_ID}
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                helpText="Azure AD Tenant ID"
                canEdit={canEditSettings}
              />
              <InputField
                label="Client ID"
                type="text"
                value={emailSettings.GRAPH_CLIENT_ID}
                onChange={(e) => {
                  setEmailSettings(prevSettings => ({
                    ...prevSettings,
                    GRAPH_CLIENT_ID: e.target.value
                  }));

                  if (emailErrors.GRAPH_CLIENT_ID) {
                    setEmailErrors(prevErrors => ({ ...prevErrors, GRAPH_CLIENT_ID: '' }));
                  }
                }}
                error={emailErrors.GRAPH_CLIENT_ID}
                placeholder="Azure App Client ID"
                helpText="Azure App Client ID"
                canEdit={canEditSettings}
              />
              <InputField
                label="Client Secret"
                type="password"
                value={emailSettings.GRAPH_CLIENT_SECRET}
                onChange={(e) => {
                  setEmailSettings(prevSettings => ({
                    ...prevSettings,
                    GRAPH_CLIENT_SECRET: e.target.value
                  }));

                  if (emailErrors.GRAPH_CLIENT_SECRET) {
                    setEmailErrors(prevErrors => ({ ...prevErrors, GRAPH_CLIENT_SECRET: '' }));
                  }
                }}
                error={emailErrors.GRAPH_CLIENT_SECRET}
                placeholder="Azure Client Secret"
                helpText="Azure Application Client Secret"
                canEdit={canEditSettings}
              />
            </div>
          </SettingsSection>
        </div>

        {emailMessage.text && (
          <div
            className={`settings-email-message settings-email-message--${emailMessage.type}`}
            role={emailMessage.type === 'error' ? 'alert' : 'status'}
            aria-live="polite"
          >
            {emailMessage.type === 'success' ? (
              <CheckCircle aria-hidden="true" />
            ) : (
              <AlertTriangle aria-hidden="true" />
            )}
            <p>{emailMessage.text}</p>
          </div>
        )}

        <div className="settings-email-actions">
          <button
            type="button"
            onClick={saveEmailSettings}
            disabled={!canEditSettings || emailLoading}
            className="settings-email-save"
            title={!canEditSettings ? 'You do not have permission to edit settings' : ''}
          >
            {emailLoading ? (
              <Loader className="animate-spin" aria-hidden="true" />
            ) : (
              <Save aria-hidden="true" />
            )}
            {emailLoading ? 'Saving...' : 'Save Email Settings'}
          </button>
        </div>
      </div>

      <ConfirmationDialog
        open={confirmationDialog.open}
        onClose={() => setConfirmationDialog({ ...confirmationDialog, open: false })}
        onConfirm={confirmationDialog.onConfirm}
        title={confirmationDialog.title}
        message={confirmationDialog.message}
        confirmText="Save Changes"
        cancelText="Cancel"
        isDangerous={false}
      />
    </>
  );
};

export default EmailSettings;
